'use client';

import React, { type CSSProperties } from 'react';
import { Menu } from 'components/Menu';
import { Text } from 'components/Text';
import { getBorderColor } from 'theme-utils';
import type { IconProps } from '@frameio/vapor-icons';
import { IconMoreHorizontal, IconX } from '@frameio/vapor-icons';
import type { ThemeColorsBorder } from '@frameio/vapor-theme';
import { IconButton2 } from '../Button2';
import styles from './Sheet.module.css';
import { useSheetContext } from './SheetContext';

type SheetHeadProps = {
  /** Override the bottom border color. */
  bottomBorderColor?: ThemeColorsBorder;
  /** An optional icon used to render the close button */
  closeIcon?: React.ReactElement<IconProps>;
  /** A label for the collapse button. */
  collapseButtonLabel?: string;
  /** Menu items for the context menu */
  contextMenuContent?: React.ReactNode;
  /** Label for context menu */
  contextMenuLabel?: string;
  /** The title of the sheet */
  title: React.ReactNode;
  /** An optional toolbar */
  toolbar?: React.ReactNode;
};

export function SheetHead({
  bottomBorderColor = 'secondary',
  closeIcon = <IconX />,
  collapseButtonLabel = 'Collapse',
  contextMenuContent = null,
  contextMenuLabel = 'More options',
  title,
  toolbar,
}: SheetHeadProps) {
  const context = useSheetContext();

  const cssVars = {
    '--vapor-sheet-head-border-color':
      getBorderColor(bottomBorderColor) ?? bottomBorderColor,
  } as CSSProperties;

  return (
    <div
      className={styles.sheetHeadContainer}
      data-testid="sheet-header-container"
      style={cssVars}
    >
      <Text
        color="primary"
        id={context.id ?? undefined}
        style={{
          flexGrow: 1,
        }}
        truncate
        variant="link"
      >
        {title}
      </Text>
      {toolbar && <div>{toolbar}</div>}
      {contextMenuContent && (
        <Menu content={contextMenuContent} placement="bottom-start">
          <IconButton2
            data-testid="sheet-context-menu-button"
            icon={<IconMoreHorizontal />}
            label={contextMenuLabel}
            size="small"
            variant="tertiary"
          />
        </Menu>
      )}
      <IconButton2
        icon={closeIcon}
        label={collapseButtonLabel}
        onPressStart={context.onRequestClose}
        size="small"
        variant="tertiary"
      />
    </div>
  );
}
